import { TEXT } from "../configs/text";
import { useClassStore, useLangStore, useSessionStore } from "../stores";
import { AccessTokenBody } from "../types/auth";
import { ClassGroupInfo, ClassType } from "../types/class";
import { dateTimeLocales } from "../types/lang";
import { Session, sessionMap, RewardPoint } from "../types/session";

export function decodeJwt(token: string | null): AccessTokenBody | null {
    if (!token) return null;

    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
}

export function formatTimestampToArr(timestamp: number | null | undefined, withSeconds?: boolean): string[] {
    if (!timestamp) return [];

    var a = new Date(timestamp * 1000);
    var months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    var year = a.getFullYear();
    var month = months[a.getMonth()];
    var date = a.getDate();
    var hour = a.getHours();
    var min = a.getMinutes();
    var sec = a.getSeconds();
    const addZero = (num: number) => {
        return (num < 10) ? '0' + num : num.toString();
    }
    const dateArr = [addZero(date), addZero(month), addZero(year)]
    const timeArr = [addZero(hour), addZero(min)]
    if (withSeconds) {
        timeArr.push(addZero(sec))
    }

    return [dateArr.join('/'), timeArr.join(':')]
}

export function parseTimestampToDateWithMonthName(timestamp: number | null | undefined): string {
    if (!timestamp) return ''
    const date = new Date(timestamp * 1000);
    const langStore = useLangStore();
    const options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "short",
        day: "numeric",
    };
    return `${date.toLocaleDateString(dateTimeLocales[langStore.locale], options)}`
}

export function formatTimestampToDate(timestamp: number | null | undefined): string {
    const [dateFormat] = formatTimestampToArr(timestamp)
    return dateFormat
}

export function formatTimestampToTime(timestamp: number | null | undefined, withSeconds?: boolean): string {
    const [_, timeFormat] = formatTimestampToArr(timestamp, withSeconds)
    return timeFormat
}

export function formatTimestampToDateTime(timestamp: number | null | undefined, withSeconds?: boolean): string {
    const [dateFormat, timeFormat] = formatTimestampToArr(timestamp, withSeconds)
    if (timeFormat === '00:00:00' || timeFormat === '00:00') {
        return dateFormat
    }

    return `${dateFormat} - ${timeFormat}`
}

export function formatTimeStringNoSeconds(timeString: string | null | undefined): string {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':')
    return `${hours}:${minutes}`
}

export function calculateTotalPoint(point: RewardPoint[] | undefined | null): number {
    if (!point) return 0;

    const totalPoint = point.reduce((totalPoint, item) => totalPoint + (item.point || 0), 0);

    return totalPoint
}

export function calculateGroupTotalPoint(group: ClassGroupInfo): number {
    const groupPoint = calculateTotalPoint(group.bonus_point)
    const studentPoints = group.students?.reduce((total, student) => {
        return total += calculateTotalPoint(student.bonus_point)
    }, 0) || 0

    return groupPoint + studentPoints
}

export function timeStringToMilliseconds(timeString: string): number {
    const [hours, minutes, seconds] = timeString.split(':').map(Number);

    if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
        return 0
    }

    // Convert to milliseconds
    return (hours * 3600 + minutes * 60 + seconds) * 1000;
}

export function ordinalSuffixOf(i: number | undefined): string {
    if (!i) return '--';
    let j = i % 10,
        k = i % 100;
    if (j === 1 && k !== 11) {
        return i + "st";
    }
    if (j === 2 && k !== 12) {
        return i + "nd";
    }
    if (j === 3 && k !== 13) {
        return i + "rd";
    }
    return i + "th";
}

export function stripHtmlTags(content: string): string {
    return content.replace(/<\/?[^>]+(>|$)/g, "")
}

export function decodeHtmlEntities(str: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(str, 'text/html');
    return doc.documentElement.textContent || '';
}

export function isValidUrl(url: string): boolean {
    try {
        const parsedUrl = new URL(url)
        return Boolean(parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:")
    } catch {
        return false
    }
}

export function containsEmptyString(arr: string[]): boolean {
    return arr.some(item => item === '');
}

export function formatSecondsToMinuteSeconds(seconds: number, format: 'mm:ss' | 'mmms' = 'mm:ss'): string {
    if (!seconds) return format === 'mm:ss' ? '00:00' : '00m00s';

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(remainingSeconds).padStart(2, '0');

    return format === 'mm:ss'
        ? `${formattedMinutes}:${formattedSeconds}`
        : `${formattedMinutes}m${formattedSeconds}s`;
}

export function debounce<T extends (...args: any[]) => any>(fn: T, delay = 500): (...args: Parameters<T>) => void {
    let timeout: ReturnType<typeof setTimeout>
    return (...args: Parameters<T>) => {
        clearTimeout(timeout)
        timeout = setTimeout(() => {
            fn(...args)
        }, delay)
    }
}

export function roundScore(score: number): number {
    return Math.round(score * 2) / 2
}

//compare version
function parseVersion(version: string) {
    const [main, build] = version.split("+");
    const [core, pre] = main.split("-");
    const [major, minor, patch] = core.split(".").map(n => parseInt(n, 10) || 0);

    const preParts = pre ? pre.split(".") : [];

    return { major, minor, patch, preParts, build };
}

function compareIdentifiers(a: string, b: string): number {
    const isNumA = /^\d+$/.test(a);
    const isNumB = /^\d+$/.test(b);

    if (isNumA && isNumB) {
        const diff = parseInt(a, 10) - parseInt(b, 10);
        return diff === 0 ? 0 : diff > 0 ? 1 : -1;
    }
    if (isNumA) return -1; // numeric < non-numeric
    if (isNumB) return 1;  // non-numeric > numeric
    return a === b ? 0 : a < b ? -1 : 1;
}

/**
 * @returns -1 if v1 < v2, 0 if equals, 1 if v1 > v2
 */
export function checkVersion(v1: string, v2: string): number {
    const a = parseVersion(v1);
    const b = parseVersion(v2);

    if (a.major !== b.major) return a.major > b.major ? 1 : -1;
    if (a.minor !== b.minor) return a.minor > b.minor ? 1 : -1;
    if (a.patch !== b.patch) return a.patch > b.patch ? 1 : -1;

    if (!a.preParts.length && b.preParts.length) return 1;
    if (a.preParts.length && !b.preParts.length) return -1;

    for (let i = 0; i < Math.max(a.preParts.length, b.preParts.length); i++) {
        const idA = a.preParts[i];
        const idB = b.preParts[i];
        if (idA === undefined) return -1;
        if (idB === undefined) return 1;

        const cmp = compareIdentifiers(idA, idB);
        if (cmp !== 0) return cmp;
    }

    return 0;
}

export function extractUrls(data: any): string[] {
    const urls: string[] = []
    if (!data) return urls

    function walk(obj: any) {
        if (obj == null) return

        if (typeof obj === "string") {
            if (/^https?:\/\//.test(obj)) {
                urls.push(encodeURI(obj))
            }
        } else if (Array.isArray(obj)) {
            obj.forEach((item) => walk(item))
        } else if (typeof obj === "object") {
            Object.values(obj).forEach((value) => walk(value))
        }
    }

    walk(data)

    return urls
}

export function mapCDNUrls(data: any) {
    if (!data) return data

    function mapUrls(value: any): any {
        if (value == null) return value

        if (typeof value === "string") {
            return value
                .replace(
                    /https?:\/\/itov3-s3-cms-lcms\.s3(\.ap-southeast-1)?\.amazonaws\.com\/uploads/gi,
                    'https://iclass-cdn2.ismart.edu.vn'
                )
                .replace(
                    /https?:\/\/ismart-online-testing\.s3(\.ap-southeast-1)?\.amazonaws\.com\/uploads/gi,
                    'https://iclass-cdn1.ismart.edu.vn'
                )
        }

        if (Array.isArray(value)) {
            return value.map(item => mapUrls(item))
        }

        if (typeof value === "object") {
            const result: any = {};
            Object.keys(value).forEach(key => {
                result[key] = mapUrls(value[key])
            });
            return result
        }

        return value
    }

    return mapUrls(data);
}

export function preloadFromApiData(data: any) {
    const urls = extractUrls(data)
    const speculationRules = {
        ['prefetch']: [{
            urls: urls
        }]
    };

    // TODO: find more reliable caching method
    // Check for browser support speculationrules
    if (HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules')) {
        const script = document.createElement('script')
        script.type = 'speculationrules'
        script.textContent = JSON.stringify(speculationRules)
        document.body.appendChild(script)
        return
    }

    // Fallback to Method 2 for unsupported browsers
    urls.forEach(url => {
        const link = document.createElement('link')
        link.rel = 'prefetch'
        link.href = url
        document.head.appendChild(link)
    })
}

export function getSessionName(session: Session): string {
    const sessionStore = useSessionStore()
    const classStore = useClassStore()
    const isConcept = classStore.selectedClassDetails?.desc === ClassType.igen

    if (session === Session.custom) {
        return sessionStore.classContent?.[session]?.custom_section_name || sessionMap[session]
    }

    if (session === Session.vocab && isConcept) {
        return TEXT.concept_session;
    }

    return sessionMap[session]
}

export function getBlobDuration(blob: Blob): Promise<number> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = async (event) => {
            try {
                const arrayBuffer = event.target?.result;
                if (!(arrayBuffer instanceof ArrayBuffer)) {
                    return reject(new Error("Invalid ArrayBuffer"));
                }

                const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
                const audioContext = new AudioCtx();

                const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
                resolve(audioBuffer.duration); // đơn vị: giây
            } catch (error) {
                reject(error);
            }
        };

        reader.onerror = () => {
            reject(new Error("Failed to read blob"));
        };

        reader.readAsArrayBuffer(blob);
    });
}

export async function getBlobDurationOffline(blob: Blob): Promise<number> {
    if (!blob || !(blob instanceof Blob)) return 0;

    const arrayBuffer = await blob.arrayBuffer()
    const OfflineAudioCtx = (window as any).OfflineAudioContext || (window as any).webkitOfflineAudioContext
    const offline = new OfflineAudioCtx(1, 1, 44100);
    const audioBuffer = await offline.decodeAudioData(arrayBuffer)
    return audioBuffer.duration
}

export function toBase64(data: any): string {
    const json = JSON.stringify(data)
    if (json === undefined) return ''
    const bytes = new TextEncoder().encode(json)
    let binary = ''
    for (let i = 0; i < bytes.length; i += 0x8000) {
        binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
    }
    return window.btoa(binary)
}
